import { clsx } from 'clsx';

import { Icons } from '@monoimin/atoms';

import { getInputGrid } from '@monoimin/utils/helpers';

import type { InputProps } from '@monoimin/forms/gravity-form/types';

export const FileInput = ({
  adminLabel,
  isRequired,
  customId,
  layoutGridColumnSpan = 12,
  allowedExtensions,
  fileUploads,
  formValuesById,
  id,
}: InputProps) => {
  const gridColumn = getInputGrid(layoutGridColumnSpan);

  const inputName = adminLabel || customId;

  const { status, progress } = fileUploads?.[customId] || {
    status: 'idle', // 'loading', // 'success', //
    progress: 0, // 75, //
  };

  const isUploaded = status === 'success';
  const actualProgress = isUploaded ? 100 : progress;
  const isUploading = status === 'loading';
  const isIdle = status === 'idle';
  const isError = status === 'error';

  return (
    <div
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='9' ry='9' stroke='%23333' stroke-width='1.5' stroke-dasharray='14%2c 8' stroke-dashoffset='4' stroke-opacity='0.5' strokeLinecap='round'/%3e%3c/svg%3e")`,
      }}
      className={clsx(
        'relative min-h-[220px] rounded-lg p-0.5',
        gridColumn,
        isUploaded && 'bg-blueGrey'
      )}
    >
      <div className='w-full h-full relative flex flex-col items-center justify-center'>
        {isError ? (
          <div
            data-testid='file-input-error'
            className='flex flex-col items-center gap-2'
          >
            <div className='flex items-center justify-center w-8 h-8 mb-0.5 object-contain bg-red text-white rounded-full border'>
              <FileUploadedIcon />
            </div>
            <span className='text-lg lg:text-xl text-blue-500 text-center'>
              Error occured, please try upload file again or reload the page!
            </span>
          </div>
        ) : null}
        {isUploaded ? (
          <div className='flex flex-col items-center'>
            <div className='flex items-center justify-center w-16 h-16 mb-4 object-contain bg-blue-400 text-white rounded-full border'>
              <FileUploadedIcon />
            </div>
            <span className='text-xl text-black font-bold mb-1'>
              File uploaded, thank you!
            </span>
            <span className='text-xl text-black'>
              {(formValuesById[id] as File)?.name || 'File name'}
            </span>
          </div>
        ) : null}

        {actualProgress > 0 && isUploading ? (
          <div className='flex flex-col items-center mb-2'>
            <div
              style={{
                height: '3.3rem',
                width: '3.3rem',
              }}
              className='relative mb-4 flex items-center justify-center'
            >
              <div className='absolute w-12 h-12'>
                <Passport className='w-full h-full stroke-blue-500' />
              </div>
              <CirclePieChart
                percentage={actualProgress}
                className='w-full h-full relative'
              />
            </div>
            <span className='text-xl text-black font-bold'>
              {(formValuesById[id] as File)?.name || 'Empty file name'}
            </span>
            <label className='text-xl mt-1' htmlFor={customId}>
              uploading...
            </label>
          </div>
        ) : null}
        {isIdle ? (
          <div className='flex flex-col items-center'>
            <div className='w-12 h-12 mb-4'>
              <Passport />
            </div>

            <label className='text-xl font-bold mb-1' htmlFor={customId}>
              Drag files here to begin
            </label>
            <span className='text-xl'>
              or you can{' '}
              <span className='text-blue-500 inline-flex items-start'>
                pick files to upload{' '}
                <Icons
                  className='ml-0.5 w-3 h-3 stroke-blue-500'
                  icon='download'
                />
              </span>
            </span>
          </div>
        ) : null}

        <input
          id={customId}
          type={'file'}
          name={inputName}
          required={isRequired}
          accept={'.' + allowedExtensions?.split(', ').join(', .')}
          className={'absolute inset-0 appearance-none opacity-0'}
          data-testid='file-input'
        />
        {isUploaded && (
          <button
            onClick={(e) => {
              e.preventDefault();
              const input = document.getElementById(
                customId
              ) as HTMLInputElement | null;
              if (input) {
                input.value = '';

                const event = new Event('input', {
                  bubbles: true,
                });
                input.dispatchEvent(event);
              }
            }}
            data-testid='delete-file-button'
            className='relative flex items-center gap-1 text-xl text-blue-500 cursor-pointer mt-1'
          >
            Delete file <Bucket className='w-6 h-6' />
          </button>
        )}
      </div>
    </div>
  );
};

const FileUploadedIcon = () => (
  <svg
    className='w-10 h-10'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M13 20H7C6.44772 20 6 19.5523 6 19V5C6 4.44772 6.44772 4 7 4H12.5576C12.8394 4 13.108 4.11885 13.2976 4.32733L17.7399 9.21393C17.9073 9.39801 18 9.63784 18 9.88661V14'
      stroke='#F2F6F9'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M13 5V9C13 9.55228 13.4477 10 14 10H18'
      stroke='#F2F6F9'
      strokeLinecap='round'
    />
    <line
      x1='8.5'
      y1='16.5'
      x2='12.5'
      y2='16.5'
      stroke='#F2F6F9'
      strokeLinecap='round'
    />
    <line
      x1='8.5'
      y1='13.5'
      x2='15.5'
      y2='13.5'
      stroke='#F2F6F9'
      strokeLinecap='round'
    />
    <line
      x1='8.5'
      y1='10.5'
      x2='10.5'
      y2='10.5'
      stroke='#F2F6F9'
      strokeLinecap='round'
    />
    <path
      d='M15 18L17 20L21 16'
      stroke='#F2F6F9'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const circleConfig = {
  viewBox: `0 0 38 38`,
  x: `19`,
  y: `19`,
  radio: `15.91549430918954`,
};
type CirclePieChartProps = {
  className?: string;
  percentage: number;
  // children: ReactNode;
  isDark?: boolean;
};
const CirclePieChart = ({
  className,
  percentage,
  // children,
  isDark = false,
}: CirclePieChartProps) => (
  <figure className={className} style={{ maxWidth: `170px` }}>
    <svg viewBox={circleConfig.viewBox}>
      <circle
        className='ring'
        cx={circleConfig.x}
        cy={circleConfig.y}
        r={circleConfig.radio}
        fill='transparent'
        stroke={isDark ? `#636363` : `#EBEBEB`}
        strokeWidth={1.2}
      />
      {percentage > 0 ? (
        <circle
          className='path'
          cx={circleConfig.x}
          cy={circleConfig.y}
          r={circleConfig.radio}
          fill='transparent'
          stroke='url(#linearColor)'
          strokeDasharray={`${percentage} ${100 - percentage}`}
          strokeDashoffset={75}
          strokeWidth={1.2}
          strokeLinecap='round'
          style={{
            filter: `url(#dropshadow)`,
            animation: `pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
          }}
        />
      ) : null}

      <g x='0%' y='50%' className=''>
        {/* <text x='0%' y='50%' fill='#999999' className='circle-percentage'> */}

        {/* </text> */}
      </g>
      <filter id='dropshadow'>
        <feGaussianBlur in='SourceAlpha' stdDeviation='0.4' />
        <feOffset dx='0' dy='0.5' result='offsetblur' />
        <feComponentTransfer>
          <feFuncA type='linear' slope='0.5' />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in='SourceGraphic' />
        </feMerge>
      </filter>
      <linearGradient id='linearColor' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stopColor={isDark ? '#2078FF' : '#001A70'} />
        <stop offset='100%' stopColor={isDark ? '#2078FF' : '#001A70'} />
      </linearGradient>
    </svg>
  </figure>
);

const Bucket = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox='0 0 25 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M5.5 5.96301L7.10686 18.2302C7.10686 19.3214 9.93441 20.206 12.9092 20.206C15.884 20.206 18.7115 19.3214 18.7115 18.2302L20.3184 5.96301'
      stroke='#004792'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M9.04419 4.49585C6.91834 4.84314 5.5 5.46832 5.5 6.18183C5.5 7.27302 8.81721 8.1576 12.9092 8.1576C17.0012 8.1576 20.3184 7.27302 20.3184 6.18183C20.3184 5.46832 18.9001 4.84314 16.7742 4.49585'
      stroke='#004792'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M14.8977 5.49995L14.7692 4.35976C14.7228 4.14973 14.5365 4 14.3212 4H11.4964C11.2812 4 11.095 4.14952 11.0485 4.35976L10.9199 5.49995'
      stroke='#004792'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M16.8133 10.2759L16.1895 17.5183'
      stroke='#004792'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12.9102 10.2759V17.7263'
      stroke='#004792'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M9.00586 10.2759L9.62972 17.5183'
      stroke='#004792'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const Passport = (_: { className?: string }) => (
  <div
    className={
      'w-full h-full rounded-full bg-blue-50 flex items-center justify-center relative'
    }
  >
    <svg
      className={'w-6 h-6 stroke-blue-500'}
      viewBox='0 0 18 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M1.10039 23.5H16.4338C16.5783 23.5 16.6969 23.4515 16.7733 23.383C16.8426 23.321 16.9004 23.2231 16.9004 23.0609V0.939131C16.9004 0.682658 16.7072 0.5 16.4338 0.5H1.10039V23.5Z'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path
        d='M9.05504 15.0758C12.2934 15.0758 14.9187 12.4505 14.9187 9.21214C14.9187 5.97375 12.2934 3.34851 9.05504 3.34851C5.81665 3.34851 3.19141 5.97375 3.19141 9.21214C3.19141 12.4505 5.81665 15.0758 9.05504 15.0758Z'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path
        d='M9.27344 3.13037V15.1304'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path
        d='M10.6641 3.56665C10.6641 3.56665 12.3822 5.80301 12.3822 9.21211C12.3822 12.6212 10.9368 14.7757 10.9368 14.7757'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path
        d='M7.71818 3.56665C7.71818 3.56665 6 5.80301 6 9.21211C6 12.6212 7.44545 14.7757 7.44545 14.7757'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path d='M3 9.13037H15' stroke='#001A70' strokeMiterlimit='10' />
      <path
        d='M3.81836 6.40308H14.182'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path
        d='M3.81836 11.8577H14.182'
        stroke='#001A70'
        strokeMiterlimit='10'
      />
      <path d='M3 18.2609H15' stroke='#001A70' strokeMiterlimit='10' />
    </svg>
  </div>
);
